"use client"

import React, { useState, useEffect, useRef } from "react"
import { useLanguage } from "../contexts/LanguageContext"
import { authAPI } from "../services/api"
import { User, Save, Camera, Edit3, Shield, Lock, Eye, EyeOff } from "lucide-react"
import { Box, Flex, Card, Grid, Heading, Text, TextField, TextArea, Button, Avatar } from "@radix-ui/themes"
import { toast } from "react-hot-toast"

const ProfilePage: React.FC = () => {
  const { t, language, dir } = useLanguage()
  const [isEditing, setIsEditing] = useState(false)
  const [loading, setLoading] = useState(false)
  const [passwordLoading, setPasswordLoading] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [profileData, setProfileData] = useState({ username: "", email: "", fullName: "", bio: "", phone: "", profileImage: "" })
  const [passwordData, setPasswordData] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" })

  useEffect(() => { loadProfile() }, [])

  const loadProfile = async () => {
    try {
      const response = await authAPI.getProfile()
      const userData = response.data
      setProfileData(prev => ({ ...prev, username: userData.username || "", email: userData.email || "", fullName: userData.fullName || "", bio: userData.bio || "", phone: userData.phone || "", profileImage: userData.profileImage || "" }))
    } catch (error) { console.error("Error loading profile:", error) }
  }

  const handleFieldChange = (field: string, value: string) => setProfileData(prev => ({ ...prev, [field]: value }))
  const handlePasswordFieldChange = (field: string, value: string) => setPasswordData(prev => ({ ...prev, [field]: value }))
  const triggerFileInput = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); if (isEditing && fileInputRef.current) fileInputRef.current.click() }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { toast.error(t('pleaseSelectImage') || 'Please select an image'); return }
    if (file.size > 5 * 1024 * 1024) { toast.error(t('imageTooLarge') || 'Image is too large'); return }
    setUploadingImage(true)
    const reader = new FileReader()
    reader.onloadend = async () => {
      try {
        const base64 = reader.result as string
        setProfileData(prev => ({ ...prev, profileImage: base64 }))
        await authAPI.updateProfileImage(base64)
        toast.success(t('profileImageUpdated') || 'Profile image updated')
      } catch (error) { toast.error(t('imageUploadFailed') || 'Image upload failed') }
      finally { setUploadingImage(false) }
    }
    reader.readAsDataURL(file)
  }

  const handleSaveProfile = async () => {
    setLoading(true)
    try {
      const response = await authAPI.updateProfile({ username: profileData.username, fullName: profileData.fullName, email: profileData.email, phone: profileData.phone, bio: profileData.bio })
      const storedUser = localStorage.getItem("admin_user")
      if (storedUser) localStorage.setItem("admin_user", JSON.stringify({ ...JSON.parse(storedUser), ...response.data.user }))
      toast.success(t('profileUpdatedSuccessfully') || 'Profile updated successfully')
      setIsEditing(false)
    } catch (error: any) { toast.error(error.response?.data?.message || t('profileUpdateFailed') || 'Profile update failed') }
    finally { setLoading(false) }
  }

  const handlePasswordChange = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) { toast.error(t('allFieldsRequired') || 'All fields are required'); return }
    if (passwordData.newPassword !== passwordData.confirmPassword) { toast.error(t('passwordsDoNotMatch') || 'Passwords do not match'); return }
    if (passwordData.newPassword.length < 6) { toast.error(t('passwordMinLength') || 'Password must be at least 6 characters'); return }
    setPasswordLoading(true)
    try {
      await authAPI.changePassword({ currentPassword: passwordData.currentPassword, newPassword: passwordData.newPassword })
      toast.success(t('passwordChangedSuccessfully') || 'Password changed successfully')
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" })
    } catch (error: any) { toast.error(error.response?.data?.message || t('passwordChangeFailed') || 'Password change failed') }
    finally { setPasswordLoading(false) }
  }

  const displayName = profileData.fullName || profileData.username || "Admin"

  return (
    <Box style={{ maxWidth: '64rem', margin: '0 auto' }} dir={dir}>
      <Flex direction="column" gap="6">
        <Card>
          <Flex justify="between" align="center" direction={language === 'ar' ? 'row-reverse' : 'row'}>
            <Flex align="center" gap="4" direction={language === 'ar' ? 'row-reverse' : 'row'}>
              <Box style={{ position: 'relative' }}>
                <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" style={{ display: 'none' }} />
                <div onClick={triggerFileInput} style={{ cursor: isEditing ? 'pointer' : 'default', position: 'relative' }}>
                  <Avatar size="6" src={profileData.profileImage || undefined} fallback={displayName.charAt(0).toUpperCase()} color="red" />
                  {isEditing && (
                    <div style={{ position: 'absolute', bottom: 0, right: language === 'ar' ? 'auto' : 0, left: language === 'ar' ? 0 : 'auto', background: 'var(--accent-9)', borderRadius: '50%', width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                      {uploadingImage ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Camera className="w-3 h-3" style={{ color: 'white' }} />}
                    </div>
                  )}
                </div>
              </Box>
              <Box><Heading size="6">{displayName}</Heading><Text size="2" color="gray">{t('manageAccountSettings')}</Text></Box>
            </Flex>
            <Button onClick={() => setIsEditing(!isEditing)} variant={isEditing ? "soft" : "solid"}><Edit3 className="w-4 h-4" />{isEditing ? t('cancel') : t('edit')}</Button>
          </Flex>
        </Card>
        <Grid columns={{ initial: "1", lg: "2" }} gap="6">
          <Card>
            <Flex direction="column" gap="4">
              <Flex align="center" gap="2" direction={language === 'ar' ? 'row-reverse' : 'row'}><User className="w-5 h-5" /><Heading size="4">{t('personalInformation')}</Heading></Flex>
              <Flex direction="column" gap="4">
                <Box><Text as="label" size="2" weight="medium">{t('fullName')}</Text><TextField.Root value={profileData.fullName} onChange={(e) => handleFieldChange('fullName', e.target.value)} disabled={!isEditing} placeholder={t('enterFullName')} /></Box>
                <Box><Text as="label" size="2" weight="medium">{t('username')}</Text><TextField.Root value={profileData.username} onChange={(e) => handleFieldChange('username', e.target.value)} disabled={!isEditing} /></Box>
                <Box><Text as="label" size="2" weight="medium">{t('emailAddress')}</Text><TextField.Root type="email" value={profileData.email} onChange={(e) => handleFieldChange('email', e.target.value)} disabled={!isEditing} /></Box>
                <Box><Text as="label" size="2" weight="medium">{t('phoneNumber')}</Text><TextField.Root value={profileData.phone} onChange={(e) => handleFieldChange('phone', e.target.value)} disabled={!isEditing} placeholder={t('enterPhoneNumber')} /></Box>
                <Box><Text as="label" size="2" weight="medium">{t('bio')}</Text><TextArea value={profileData.bio} onChange={(e) => handleFieldChange('bio', e.target.value)} disabled={!isEditing} placeholder={t('tellUsAboutYourself')} rows={3} /></Box>
                {isEditing && <Button onClick={handleSaveProfile} disabled={loading} style={{ width: '100%' }}><Save className="w-4 h-4" />{loading ? t('saving') : t('saveChanges')}</Button>}
              </Flex>
            </Flex>
          </Card>
          <Card>
            <Flex direction="column" gap="4">
              <Flex align="center" gap="2" direction={language === 'ar' ? 'row-reverse' : 'row'}><Shield className="w-5 h-5" /><Heading size="4">{t('securitySettings')}</Heading></Flex>
              <Flex direction="column" gap="4">
                <Box><Text as="label" size="2" weight="medium">{t('currentPassword')}</Text>
                  <TextField.Root type={showCurrentPassword ? "text" : "password"} value={passwordData.currentPassword} onChange={(e) => handlePasswordFieldChange('currentPassword', e.target.value)} placeholder={t('enterCurrentPassword')}>
                    <TextField.Slot side={language === 'ar' ? 'right' : 'left'}><Lock className="w-4 h-4" /></TextField.Slot>
                    <TextField.Slot side={language === 'ar' ? 'left' : 'right'}><button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>{showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button></TextField.Slot>
                  </TextField.Root>
                </Box>
                <Box><Text as="label" size="2" weight="medium">{t('newPassword')}</Text>
                  <TextField.Root type={showNewPassword ? "text" : "password"} value={passwordData.newPassword} onChange={(e) => handlePasswordFieldChange('newPassword', e.target.value)} placeholder={t('enterNewPassword')}>
                    <TextField.Slot side={language === 'ar' ? 'right' : 'left'}><Lock className="w-4 h-4" /></TextField.Slot>
                    <TextField.Slot side={language === 'ar' ? 'left' : 'right'}><button type="button" onClick={() => setShowNewPassword(!showNewPassword)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>{showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button></TextField.Slot>
                  </TextField.Root>
                </Box>
                <Box><Text as="label" size="2" weight="medium">{t('confirmPassword')}</Text>
                  <TextField.Root type="password" value={passwordData.confirmPassword} onChange={(e) => handlePasswordFieldChange('confirmPassword', e.target.value)} placeholder={t('confirmNewPassword')}>
                    <TextField.Slot side={language === 'ar' ? 'right' : 'left'}><Lock className="w-4 h-4" /></TextField.Slot>
                  </TextField.Root>
                </Box>
                <Button onClick={handlePasswordChange} disabled={passwordLoading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword} variant="soft" style={{ width: '100%' }}><Lock className="w-4 h-4" />{passwordLoading ? t('updating') : t('changePassword')}</Button>
              </Flex>
            </Flex>
          </Card>
        </Grid>
      </Flex>
    </Box>
  )
}

export default ProfilePage
